import { useTranslation } from 'react-i18next';
import { max } from 'lodash';
import { AccountData, NumberPriceData, OrderPageData, OrderPageDataTypes, OrderPageTypes } from '../../api';
import { isBookingFeeOn, quotePrice, withBookingFee } from '../../orderPage/core/pricing';
import { customerFacingPriceFormatter } from '../../orderPage/utils';
import { useLanguage } from '../../utils/GeneralUtils';

export const useStartingPriceLabel = (
  orderPage: OrderPageData,
  account: AccountData | undefined,
): { label: string; startingPrice: number } => {
  const { t } = useTranslation();
  const language = useLanguage();

  const personsPriceData = orderPage.price?.find((p) => p.data_type === OrderPageDataTypes.PERSONS) as
    | NumberPriceData
    | undefined;
  const personsBaseValue = max([personsPriceData?.base_value, orderPage.persons]) || 1;

  const { total: startingPrice } = quotePrice({
    orderPage,
    details: {
      duration: orderPage.duration,
      rooms: 1,
      persons: personsBaseValue,
      custom_details: {},
      price: 0,
    },
    dateDetails: undefined,
    account,
  });

  const perPersonPrice = startingPrice / personsBaseValue;

  if (!account || perPersonPrice <= 0) {
    return { label: '', startingPrice: perPersonPrice };
  }

  const startingTotal = withBookingFee(startingPrice, account);
  const displayPerPersonPrice = isBookingFeeOn(account)
    ? Math.round((startingTotal / personsBaseValue) * 100) / 100
    : perPersonPrice;
  const formattedStartingPrice = customerFacingPriceFormatter(startingTotal, account);
  const formattedPerPersonPrice = customerFacingPriceFormatter(displayPerPersonPrice, account);

  const activityLabel = orderPage.activity_label?.[language];
  const unitLabel = orderPage.price_unit_label?.[language];

  const isCourse = orderPage.order_page_type === OrderPageTypes.COURSE;

  const baseLabel =
    personsBaseValue > 1 && activityLabel
      ? [formattedStartingPrice, t('storefront.forActivity', { activityLabel })]
      : unitLabel
      ? [formattedPerPersonPrice, t('storefront.perUnit', { unit: unitLabel })]
      : [formattedPerPersonPrice, t('storefront.perPerson')];

  const parts = isCourse ? baseLabel : [t('storefront.startingAt'), ...baseLabel];

  if (isCourse && orderPage.course_sessions_number) {
    parts.push(t('storefront.forSessions', { count: orderPage.course_sessions_number }));
  }

  return { label: parts.join(' '), startingPrice };
};
